/**
 * @file ButtonColorFields.tsx
 * @description Заливка и рамка кнопки — в группе «Цвет и шрифт» перед цветом
 * текста (phase19.16, партия SB120, этап 3а; uiux-sb120-stage3 §3 «Кнопка»;
 * данные db-design-sb120-stage3 §2 `button.fill`, `button.border`):
 *
 * ```
 * Заливка   [Как у сайта ✓][Фон]…[Кнопки]…[⌖ Свой]       ← только «С заливкой»
 *           Наведение — темнее, считает платформа (как у кнопок сайта)
 * Рамка     ├●───┤ [ 0 ] px  [Нет 0][1][2]     ↺ как у вида
 * Цвет рамки [Авто: как текст ✓]…[⌖ Свой]                 ← при рамке > 0
 * ```
 *
 * Цвет текста — `typo.main.color` в полях частей ниже («Авто: по заливке»,
 * замок SB83 снят — отдельного `textColor` нет, «Расхождения» п. 5).
 * «Ссылка» — без заливки и рамки: пояснение вместо полей. Нет `border` —
 * как у вида: у контурной 1 px цветом текста, у остальных без рамки.
 */

import { DEFAULT_SITE_THEME, SECTION_LOOK_LIMITS, type SectionButtonBorder, type SectionElementOf } from "@chaiberi/shared";

import { FieldHint } from "../../../ui/settings-groups";
import { ColorRefField } from "../../../site-fields/ColorRefField";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";

type Button = SectionElementOf<"button">;

export const BUTTON_LINK_HINT = "У ссылки нет заливки и рамки — здесь только цвет и шрифт текста.";
export const BUTTON_HOVER_HINT = "Наведение — немного темнее заливки, считает платформа (как у кнопок сайта).";

interface Props {
  element: Button;
  onChange: (element: Button) => void;
  path: string;
}

export function ButtonColorFields({ element, onChange, path }: Props) {
  const { theme, preview } = useSectionForm();
  const colors = (theme ?? DEFAULT_SITE_THEME).colors;
  if (element.variant === "link") return <FieldHint className="mt-0">{BUTTON_LINK_HINT}</FieldHint>;

  const outline = element.variant === "outline";
  const viewWidth = outline ? 1 : 0;
  const width = element.border?.width ?? viewWidth;
  const setBorder = (border: SectionButtonBorder | undefined) => {
    const next = { ...element };
    if (border) next.border = border.color ? border : { width: border.width };
    else delete next.border;
    onChange(next);
  };
  const setFill = (fill: Button["fill"]) => {
    const next = { ...element };
    if (fill) next.fill = fill;
    else delete next.fill;
    onChange(next);
  };
  const B = SECTION_LOOK_LIMITS.buttonBorder;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {element.variant === "primary" ? (
        <div className="flex min-w-0 flex-col gap-1">
          <ColorRefField label="Заливка" value={element.fill} onChange={setFill} colors={colors} autoLabel="Как у сайта" />
          <FieldHint className="mt-0">{BUTTON_HOVER_HINT}</FieldHint>
        </div>
      ) : null}
      <NumberSliderField
        label="Рамка"
        integer
        value={element.border?.width}
        inherit={{ value: viewWidth, label: outline ? "как у вида" : "без рамки" }}
        min={B.min}
        max={B.max}
        step={1}
        unit="px"
        presets={[
          { label: "Нет 0", value: 0 },
          { label: "1", value: 1 },
          { label: "2", value: 2 },
        ]}
        onChange={(w) => setBorder(w === undefined ? undefined : { width: w, ...(element.border?.color ? { color: element.border.color } : {}) })}
        onPreview={(v) => preview(`${path}.border.width`, v ?? viewWidth)}
      />
      {width > 0 ? (
        <ColorRefField
          label="Цвет рамки"
          value={element.border?.color}
          onChange={(color) => setBorder({ width: element.border?.width ?? viewWidth, ...(color ? { color } : {}) })}
          colors={colors}
          autoLabel="Авто: как текст"
        />
      ) : null}
    </div>
  );
}
